import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, Minus, Plus, Sparkles, ExternalLink, Upload } from "lucide-react";
import { API, inr, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const GARMENTS = {
  tshirt: { label: "T-Shirt", img: "https://static.prod-images.emergentagent.com/jobs/16834d5d-e79f-4002-90dd-d0b839217f23/images/d0c5d3bcf6c4b594652349802dbf99e5c98d6e43845f5e78cca5af0f55757e2f.jpeg",
    bases: [{ gsm: 180, price: 249 }, { gsm: 220, price: 299 }, { gsm: 240, price: 499 }, { gsm: 260, price: 549 }] },
  hoodie: { label: "Hoodie / Sweatshirt", img: "https://static.prod-images.emergentagent.com/jobs/16834d5d-e79f-4002-90dd-d0b839217f23/images/c0f28e6986cefd92563f6f19547220186388f8491f796469e6685e4feee18bf5.jpeg",
    bases: [{ gsm: 260, price: 549 }, { gsm: 280, price: 599 }, { gsm: 300, price: 699 }, { gsm: 340, price: 899 }] },
};
const COLLECTIONS = {
  azura: { label: "Azura", desc: "Fully printed & customized — bold all-over artwork", fee: 199 },
  minimalism: { label: "Minimalism", desc: "Minimal prints — clean marks, small logos", fee: 149 },
};
const SIZES = ["S", "M", "L", "XL", "XXL"];
const COLORS = ["Black", "White", "Charcoal", "Beige", "Orange", "Olive", "Navy"];
const COLOR_HEX = {
  Black: "#262626", White: "#ffffff", Charcoal: "#4b4b52", Beige: "#e6d5b8",
  Orange: "#f97316", Olive: "#6b7a3f", Navy: "#1e3a5f",
};
const TEXT_COLORS = [
  { key: "gold", hex: "#D4AF37", label: "DN Gold" },
  { key: "white", hex: "#F5F5F0", label: "White" },
  { key: "black", hex: "#0A0A0A", label: "Black" },
];
const PLACEMENTS = [
  { key: "front", label: "Front chest" },
  { key: "back", label: "Back" },
  { key: "pocket", label: "Pocket" },
];

const inputCls = "w-full bg-ink border border-[#262626] px-4 py-3 text-sm outline-none focus:border-gold transition-colors";

export default function Customize() {
  const { user } = useAuth();
  const [garment, setGarment] = useState("tshirt");
  const [gsm, setGsm] = useState(180);
  const [collection, setCollection] = useState("minimalism");
  const [size, setSize] = useState("M");
  const [color, setColor] = useState("Black");
  const [text, setText] = useState("");
  const [textColor, setTextColor] = useState("gold");
  const [placement, setPlacement] = useState("front");
  const [design, setDesign] = useState(null); // {name, url} from gallery
  const [designRef, setDesignRef] = useState(""); // free-text catalog design name/link
  const [config, setConfig] = useState({ designs: [], catalogs: [] });
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await API.post("/custom-orders/upload", fd);
      setDesign({ name: f.name, url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` });
      setDesignRef("");
      toast.success("Design uploaded — preview updated");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const g = GARMENTS[garment];
  const base = g.bases.find((b) => b.gsm === gsm) || g.bases[0];
  const col = COLLECTIONS[collection];
  const designName = design?.name || designRef.trim();
  const unit = base.price + col.fee;
  const total = unit * qty;
  const textHex = TEXT_COLORS.find((t) => t.key === textColor)?.hex;

  useEffect(() => {
    document.title = "Customize — DN Studio | Dressedin by Urbanz";
    API.get("/custom-orders/config").then((r) => setConfig(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    if (!name.trim() || !email.includes("@") || phone.replace(/\D/g, "").length < 10)
      return toast.error("Fill your name, a valid email and phone number");
    setSending(true);
    try {
      const r = await API.post("/custom-orders", {
        garment, gsm, collection, size, color, text: text.trim(), text_color: textColor,
        placement, design_name: designName, design_url: design?.url || "",
        notes, qty, name, email, phone,
      });
      setDone(r.data);
      toast.success("Custom order request received");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSending(false);
    }
  };

  if (done)
    return (
      <div className="max-w-2xl mx-auto px-4 py-24" data-testid="custom-success">
        <CheckCircle2 size={52} className="text-gold mb-6" />
        <h1 className="font-display font-black text-4xl tracking-tighter">Design locked in.</h1>
        <p className="text-muted-foreground mt-3">
          Your custom order request <span className="text-gold font-bold" data-testid="custom-ref">{done.ref}</span> is with our studio.
          We'll confirm the design and share the final quote at {done.email}.
        </p>
        <div className="border border-[#262626] bg-ink-surface p-6 mt-8 text-sm space-y-1.5">
          <p><span className="text-muted-foreground">Piece:</span> {done.garment_label} — {done.gsm} GSM · {done.size} / {done.color}</p>
          <p><span className="text-muted-foreground">Collection:</span> {done.collection_label}</p>
          {done.text && <p><span className="text-muted-foreground">Print:</span> "{done.text}" ({done.text_color}, {done.placement})</p>}
          {done.design_name && <p><span className="text-muted-foreground">Design:</span> {done.design_name}</p>}
          <p><span className="text-muted-foreground">Qty:</span> {done.qty}</p>
          <p className="font-display font-bold text-lg text-gold pt-2">Est. total {inr(done.total)}</p>
        </div>
        <button onClick={() => { setDone(null); setText(""); setNotes(""); setDesign(null); setDesignRef(""); }} data-testid="custom-another-btn"
          className="btn-gold mt-8 px-8 py-4 font-display font-bold text-sm uppercase tracking-[0.2em]">
          Design another
        </button>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="customize-page">
      <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-2 flex items-center gap-2"><Sparkles size={13} /> DN Studio</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Make it <span className="gold-text">yours.</span></h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-lg">
        Customize your T-shirts, hoodies and sweatshirts — your fabric, your print, your identity. Made to order in Bhopal.
      </p>

      <div className="grid lg:grid-cols-2 gap-10 mt-12">
        {/* Live preview */}
        <div className="relative border border-[#262626] bg-ink-surface overflow-hidden aspect-[4/5] lg:sticky lg:top-28 h-fit" data-testid="custom-preview">
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 35%, #1c1c1c 0%, #0A0A0A 70%)" }} />
          <img src={g.img} alt={`${g.label} mockup`}
            className="absolute inset-6 w-[calc(100%-3rem)] h-[calc(100%-3rem)] object-contain" />
          <div className="absolute inset-6 w-[calc(100%-3rem)] h-[calc(100%-3rem)] mix-blend-multiply transition-colors duration-300"
            style={{
              backgroundColor: COLOR_HEX[color],
              WebkitMaskImage: `url(${g.img})`, maskImage: `url(${g.img})`,
              WebkitMaskSize: "contain", maskSize: "contain",
              WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
              WebkitMaskPosition: "center", maskPosition: "center",
              WebkitMaskMode: "luminance", maskMode: "luminance",
            }}
            data-testid="color-tint" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          {design?.url && (
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.img key={design.url} src={design.url} alt={design.name} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                data-testid="preview-design"
                className="max-h-[45%] max-w-[60%] object-contain drop-shadow-[0_2px_24px_rgba(0,0,0,0.7)]" />
            </div>
          )}
          {text.trim() && !design?.url && (
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.p key={text + textColor} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                data-testid="preview-text"
                className="font-display font-black text-3xl sm:text-4xl uppercase tracking-widest text-center px-6"
                style={{ color: textHex, textShadow: "0 2px 24px rgba(0,0,0,0.7)" }}>
                {text}
              </motion.p>
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between">
            <div>
              <p className="font-display font-bold text-lg">Custom {g.label}</p>
              <p className="text-xs text-muted-foreground">{base.gsm} GSM · {col.label} · {size} · {color}</p>
            </div>
            <p className="font-display font-black text-2xl gold-text" data-testid="custom-total">{inr(total)}</p>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">1 · Pick your piece</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(GARMENTS).map(([k, v]) => (
                <button key={k} onClick={() => { setGarment(k); setGsm(v.bases[0].gsm); }} data-testid={`garment-${k}`}
                  className={`border p-4 text-left transition-colors ${garment === k ? "border-gold bg-gold/5" : "border-[#262626] hover:border-gold/50"}`}>
                  <p className="font-display font-bold text-sm">{v.label}</p>
                  <p className="text-xs text-gold mt-1">from {inr(v.bases[0].price)}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">2 · Fabric weight</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="gsm-options">
              {g.bases.map((b) => (
                <button key={b.gsm} onClick={() => setGsm(b.gsm)} data-testid={`gsm-${b.gsm}`}
                  className={`border p-3 text-left transition-colors ${gsm === b.gsm ? "border-gold bg-gold/5" : "border-[#262626] hover:border-gold/50"}`}>
                  <p className="font-display font-bold text-sm">{b.gsm} <span className="text-[10px] text-muted-foreground">GSM</span></p>
                  <p className="text-xs text-gold mt-0.5">{inr(b.price)}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">3 · Collection</p>
            <div className="grid grid-cols-2 gap-3" data-testid="collection-options">
              {Object.entries(COLLECTIONS).map(([k, c]) => (
                <button key={k} onClick={() => setCollection(k)} data-testid={`collection-${k}`}
                  className={`border p-4 text-left transition-colors ${collection === k ? "border-gold bg-gold/5" : "border-[#262626] hover:border-gold/50"}`}>
                  <p className="font-display font-bold text-sm">{c.label} <span className="text-gold text-xs">+{inr(c.fee)}</span></p>
                  <p className="text-[11px] text-muted-foreground mt-1">{c.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">4 · Size & color</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {SIZES.map((s) => (
                <button key={s} onClick={() => setSize(s)} data-testid={`custom-size-${s}`}
                  className={`h-10 w-13 px-3 text-sm border transition-colors ${size === s ? "border-gold bg-gold text-ink font-bold" : "border-[#262626] hover:border-gold/60"}`}>
                  {s}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setColor(c)} data-testid={`custom-color-${c.toLowerCase()}`}
                  className={`px-4 py-2 text-xs border transition-colors ${color === c ? "border-gold text-gold font-bold" : "border-[#262626] text-muted-foreground hover:border-gold/60"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">5 · Your print <span className="text-muted-foreground normal-case tracking-normal">(optional text — included in collection)</span></p>
            <input data-testid="custom-text-input" value={text} onChange={(e) => setText(e.target.value.slice(0, 24))}
              placeholder="Your name, slogan, crew tag..." maxLength={24} className={inputCls} />
            {text.trim() && (
              <div className="flex flex-wrap gap-4 mt-3">
                <div className="flex gap-2">
                  {TEXT_COLORS.map((t) => (
                    <button key={t.key} onClick={() => setTextColor(t.key)} data-testid={`text-color-${t.key}`} title={t.label}
                      className={`h-8 w-8 border-2 transition-colors ${textColor === t.key ? "border-gold" : "border-[#262626]"}`}
                      style={{ background: t.hex }} />
                  ))}
                </div>
                <select data-testid="placement-select" value={placement} onChange={(e) => setPlacement(e.target.value)}
                  className="bg-ink border border-[#262626] px-3 py-1.5 text-xs outline-none focus:border-gold">
                  {PLACEMENTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">6 · Pick a design <span className="text-muted-foreground normal-case tracking-normal">(optional)</span></p>
            <div className="grid grid-cols-4 gap-2 mb-4" data-testid="design-gallery">
              {config.designs.map((d) => (
                <button key={d.name} onClick={() => { setDesign(design?.name === d.name ? null : d); setDesignRef(""); }}
                  data-testid={`design-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative border overflow-hidden transition-colors ${design?.name === d.name ? "border-gold" : "border-[#262626] hover:border-gold/50"}`}>
                  <img src={d.url} alt={d.name} loading="lazy" className="h-full w-full object-cover aspect-square" />
                  <span className="absolute inset-x-0 bottom-0 bg-ink/85 text-[9px] uppercase tracking-wider py-1 text-center text-gold">{d.name}</span>
                </button>
              ))}
            </div>
            <div className="border border-dashed border-gold/40 p-4 mb-4 text-center hover:border-gold transition-colors" data-testid="design-upload-zone">
              <label className="cursor-pointer text-xs text-muted-foreground hover:text-gold transition-colors flex items-center justify-center gap-2">
                <Upload size={14} />
                <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="design-upload-input" />
                {uploading ? "Uploading your design..." : "Upload your own design (PNG/JPG, max 8MB — transparent PNG looks best)"}
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground mb-3">Or browse our full design catalogs and name the one you want:</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {config.catalogs.map((c) => (
                <a key={c.url} href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`catalog-${c.name.slice(0, 9).replace(/\s+/g, "-").toLowerCase()}`}
                  className="inline-flex items-center gap-1.5 border border-[#262626] px-3 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold transition-colors">
                  <ExternalLink size={12} /> {c.name}
                </a>
              ))}
            </div>
            <input data-testid="design-ref-input" value={designRef}
              onChange={(e) => { setDesignRef(e.target.value); if (e.target.value) setDesign(null); }}
              placeholder="Catalog design name / link (e.g. 'New 3' or Drive link)" className={inputCls} />
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-bold mb-3">7 · Details</p>
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm text-muted-foreground">Qty</span>
              <div className="flex items-center border border-[#262626]">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 text-muted-foreground hover:text-gold" data-testid="custom-qty-minus"><Minus size={14} /></button>
                <span className="w-8 text-center text-sm font-bold" data-testid="custom-qty">{qty}</span>
                <button onClick={() => setQty(Math.min(10, qty + 1))} className="p-2.5 text-muted-foreground hover:text-gold" data-testid="custom-qty-plus"><Plus size={14} /></button>
              </div>
            </div>
            <textarea data-testid="custom-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Notes for our studio (font style, fit tweaks...)" className={`${inputCls} mb-4`} />
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="custom-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
              <input data-testid="custom-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" className={inputCls} />
              <input data-testid="custom-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={`${inputCls} sm:col-span-2`} />
            </div>
          </div>

          <div className="border border-[#262626] bg-ink-surface p-5 flex items-center justify-between" data-testid="custom-price-summary">
            <div className="text-xs text-muted-foreground space-y-1">
              <p>{g.label} · {base.gsm} GSM — {inr(base.price)}</p>
              <p>{col.label} collection — {inr(col.fee)}</p>
              <p>× {qty}</p>
            </div>
            <p className="font-display font-black text-2xl gold-text">{inr(total)}</p>
          </div>

          <button onClick={submit} disabled={sending} data-testid="custom-submit-btn"
            className="btn-gold w-full py-4 font-display font-bold text-sm uppercase tracking-[0.2em] disabled:opacity-50">
            {sending ? "Sending to studio..." : "Request this piece"}
          </button>
          <p className="text-[11px] text-muted-foreground">Our team confirms every design manually and shares a final quote before production.</p>
        </div>
      </div>
    </div>
  );
}
